(() => {
  const elementName = 'outway-deferred-judgeme';
  if (customElements.get(elementName)) return;

  class DeferredJudgeme extends HTMLElement {
    connectedCallback() {
      if (this.started || this.observer) return;
      this.template = this.querySelector('template[data-outway-judgeme-content]');
      if (!this.template) return;

      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        const trigger = event.target.closest('[data-outway-review-keyboard]');
        if (!trigger || event.target !== trigger) return;
        event.preventDefault();
        trigger.click();
      });

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.reveal();
        }, { rootMargin: '800px 0px' });
        this.observer.observe(this);
      } else {
        this.reveal();
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.cardsObserver?.disconnect();
      this.observer = null;
    }

    async waitForCarouselStyles() {
      const baseUrl = window.jdgm?.CDN_BASE_URL;
      if (!baseUrl) return 'unavailable';
      const base = new URL(baseUrl);
      if (base.protocol !== 'https:' || base.hostname !== 'cdn.shopify.com' || !/^\/extensions\/[^/]+\/judgeme-[^/]+\/assets\/$/.test(base.pathname)) return 'unavailable';
      const url = new URL('carousels.css', base).href;
      const findStylesheet = () => [...document.querySelectorAll('link[rel="stylesheet"]')].find((stylesheet) => stylesheet.href === url);
      let link = findStylesheet();
      if (!link) {
        window.jdgm.loadCSS(url);
        link = findStylesheet();
      }
      if (!link) return 'unavailable';
      if (link.sheet && !link.disabled && (!link.media || link.media === 'all')) return 'loaded';

      return new Promise((resolve) => {
        const finish = (status) => {
          window.clearTimeout(timeout);
          link.removeEventListener('load', loaded);
          link.removeEventListener('error', failed);
          resolve(status);
        };
        const loaded = () => finish('loaded');
        const failed = () => finish('failed');
        const timeout = window.setTimeout(() => finish('timeout'), 10000);
        link.addEventListener('load', loaded, { once: true });
        link.addEventListener('error', failed, { once: true });
      });
    }

    async loadLightbox(widget) {
      const entryPoint = widget.dataset.entryPoint;
      const entryKey = widget.dataset.entryKey;
      const baseUrl = window.jdgm?.CDN_BASE_URL;
      if (entryPoint !== 'carousel_lightbox.js' || entryKey !== 'carousel-lightbox/main.js' || !baseUrl) return;
      const base = new URL(baseUrl);
      if (base.protocol !== 'https:' || base.hostname !== 'cdn.shopify.com' || !/^\/extensions\/[^/]+\/judgeme-[^/]+\/assets\/$/.test(base.pathname)) return;
      const url = new URL(entryPoint, baseUrl);

      try {
        const response = await fetch(new URL('manifest.json', base), { credentials: 'omit' });
        if (!response.ok) throw new Error(`Manifest ${response.status}`);
        const manifest = await response.json();
        const visited = new Set();
        const loadStyles = (key) => {
          if (visited.has(key)) return;
          visited.add(key);
          const entry = manifest[key];
          if (!entry) return;
          (entry.css || []).forEach((file) => {
            const stylesheetUrl = new URL(file, base);
            if (!stylesheetUrl.href.startsWith(base.href) || !stylesheetUrl.pathname.endsWith('.css')) return;
            if ([...document.querySelectorAll('link[rel="stylesheet"]')].some((link) => link.href === stylesheetUrl.href)) return;
            window.jdgm.loadCSS(stylesheetUrl.href);
          });
          (entry.imports || []).forEach(loadStyles);
        };
        loadStyles(entryKey);
      } catch (error) {
        console.warn('Outway Judge.me stylesheet initialization failed', error);
      }

      if ([...document.scripts].some((script) => script.src === url.href)) return;
      const script = document.createElement('script');
      script.type = 'module';
      script.src = url.href;
      document.head.appendChild(script);
    }

    makeCardsAccessible() {
      const cards = this.querySelectorAll('.jdgm-videos-carousel .jdgm-card');
      if (!cards.length) return;
      cards.forEach((card) => {
        const trigger = card.querySelector('.jdgm-rating-section');
        if (!trigger || trigger.hasAttribute('tabindex') || trigger.hasAttribute('role')) return;
        trigger.tabIndex = 0;
        trigger.setAttribute('role', 'button');
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.setAttribute('data-outway-review-keyboard', '');
      });
      this.dataset.outwayReady = 'true';
      this.cardsObserver?.disconnect();
    }

    async reveal() {
      if (this.started) return;
      this.started = true;
      this.dataset.outwayStarted = 'true';
      this.observer?.disconnect();

      const fragment = this.template.content.cloneNode(true);
      const widget = fragment.querySelector('.jdgm-videos-carousel');
      const blockId = widget?.dataset.jdgmBlockId;
      if (!widget || !blockId?.endsWith('__judge_me_reviews_videos_carousel_BMbyX8')) return;

      const nativeScripts = [];
      fragment.querySelectorAll('script').forEach((script) => {
        const code = script.textContent;
        const isVideoDependency = code.includes("jdgm.loadScript('video_carousel.js')");
        const isBlockInitializer = code.includes('jdgmCarouselUtils.initCarousel') && code.includes(blockId);
        if (!script.src && (!script.type || script.type === 'text/javascript') && (isVideoDependency || isBlockInitializer)) {
          nativeScripts.push({ code, nonce: script.nonce });
        }
        script.remove();
      });

      for (let attempt = 0; attempt < 200 && (!window.jdgmCarouselUtils || !window.jdgm?.loadScript || !window.jdgm?.loadCSS); attempt += 1) {
        await new Promise((resolve) => window.setTimeout(resolve, 50));
      }

      this.dataset.outwayStyles = window.jdgm?.loadCSS ? await this.waitForCarouselStyles() : 'unavailable';
      this.cardsObserver = new MutationObserver(() => this.makeCardsAccessible());
      this.cardsObserver.observe(this, { childList: true, subtree: true });
      this.replaceChildren(fragment);
      this.loadLightbox(widget);

      nativeScripts.forEach(({ code, nonce }) => {
        const script = document.createElement('script');
        if (nonce) script.nonce = nonce;
        script.textContent = code;
        this.appendChild(script);
      });
      this.makeCardsAccessible();
    }
  }

  customElements.define(elementName, DeferredJudgeme);
})();
